import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PatientLookupResponse } from "@mtform/shared";
import { ApiError, api } from "@/lib/api";
import { normalizeDateForPayload } from "@/lib/format";

// =============================================================================
// MRN + DOB lookup hook. Fires a debounced /api/patient/lookup whenever both
// fields are filled and a real DOB. Empty inputs reset the state. Returns the
// last completed result + a status flag the form can show as a small inline
// indicator ("Returning patient — info filled in").
// =============================================================================

export type LookupStatus = "idle" | "loading" | "found" | "not-found" | "error";

export interface PatientLookupState {
  status: LookupStatus;
  patient: PatientLookupResponse extends infer R
    ? R extends { found: true; patient: infer P }
      ? P
      : null
    : null;
  /** Re-fires the lookup with the current MRN + DOB. Used by the
   *  error banner's Retry button after a failed request. */
  retry: () => void;
}

interface Options {
  mrn: string;
  /** MM/DD/YYYY or YYYY-MM-DD; will be normalized internally. */
  dob: string;
  /** Reset the result if true (e.g. after the form was just submitted/reset). */
  paused?: boolean;
}

const DEBOUNCE_MS = 350;

export function usePatientLookup({ mrn, dob, paused = false }: Options): PatientLookupState {
  const [state, setState] = useState<Omit<PatientLookupState, "retry">>({
    status: "idle",
    patient: null,
  });
  // Bumped by retry() so the effect below re-runs with the same
  // MRN/DOB after a failed request.
  const [attempt, setAttempt] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Track the most recent "intent" the effect issued, so a slow first request
  // can't overwrite the result of a faster second request.
  const tokenRef = useRef(0);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (abortRef.current) abortRef.current.abort();

    if (paused) {
      setState({ status: "idle", patient: null });
      return;
    }

    const trimmedMrn = String(mrn || "").trim();
    const isoDob = normalizeDateForPayload(dob);

    if (!trimmedMrn || !isoDob) {
      setState({ status: "idle", patient: null });
      return;
    }

    const myToken = ++tokenRef.current;
    setState((s) => ({ ...s, status: "loading" }));

    timer.current = setTimeout(() => {
      const controller = new AbortController();
      abortRef.current = controller;
      api.patient
        .lookup({ mrn: trimmedMrn, dob: isoDob }, controller.signal)
        .then((res) => {
          if (myToken !== tokenRef.current) return;
          if (res.found) {
            setState({ status: "found", patient: res.patient });
          } else {
            setState({ status: "not-found", patient: null });
          }
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          if (myToken !== tokenRef.current) return;
          if (err instanceof ApiError && err.code === "RATE_LIMITED") {
            setState({ status: "error", patient: null });
            return;
          }
          console.warn("[lookup] failed:", err);
          setState({ status: "error", patient: null });
        });
    }, DEBOUNCE_MS);

    return () => {
      if (timer.current) clearTimeout(timer.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [mrn, dob, paused, attempt]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);

  return useMemo(() => ({ ...state, retry }), [state, retry]);
}
